// hooks/useDynamicStyles.js

import { useEffect, useRef } from 'react';
import { useNavbarContent } from './useNavbarContent';
import { validateCSS, validateJS } from '../utils/dynamicInjection';

const ALLOWED_VAR_KEYS = new Set([
  'sky-white', 'purple-color', 'dark-purple-color', 'white-color', 'light-purple',
  'light-purple-border', 'dark-blue-text', 'floating-orange-dot', 'rating-star-color',
  'custom-arrow-color', 'custom-arrow-hover', 'messageInput-color', 'input-border-color',
  'border-right-color', 'social-icons-color', 'navigation-bg-color', 'box-shadow-color',
  'black-bg-color', 'dark-gray-color', 'slate-gray-color', 'neutral-gray-color',
  'deep-purple-color', 'semi-transparent-black', 'semi-transparent-white',
  'semi-transparent-blue', 'light-transparent-black', 'lavender-white', 'primary-purple',
  'silver-gray', 'bright-purple', 'medium-purple', 'smart-features-bg',
  'green-color', 'bright-yellow-orange', 'warm-golden-yellow', 'light-orange-brown',
  'light-blue', 'purple', 'warm-orange',
]);

const isValidColor = (v) =>
  typeof v === 'string' &&
  (
    /^#([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(v) || // #rgb, #rrggbb, #rrggbbaa
    /^rgba?\([^)]+\)$/i.test(v)                             // rgb()/rgba()
  );

export const useDynamicStyles = () => {
  const { data: navbarData } = useNavbarContent();

  // track injected <style>/<script> tags (you had this already)
  const injectedRefs = useRef([]);
  // track which CSS vars we set on :root so we can remove them on cleanup
  const appliedVarKeysRef = useRef([]);

  useEffect(() => {
    // cleanup any previous injections / vars before re-applying new ones
    injectedRefs.current.forEach(ref => ref?.parentNode?.removeChild(ref));
    injectedRefs.current = [];
    appliedVarKeysRef.current.forEach(key =>
      document.documentElement.style.removeProperty(`--${key}`)
    );
    appliedVarKeysRef.current = [];

    if (!navbarData?.dataset?.data) return;
    const data = navbarData.dataset.data;

    // ---- helpers (unchanged from your version) ----
    const injectAllStyles = (cssString, idPrefix) => {
      if (!cssString || cssString.trim() === '') return;
      const parser = new DOMParser();
      const doc = parser.parseFromString(cssString, 'text/html');
      const styleTags = doc.querySelectorAll('style');

      if (styleTags.length === 0 && cssString.trim().startsWith('<style') === false) {
        const validation = validateCSS(cssString);
        if (!validation.valid) {
          return;
        }
        const style = document.createElement('style');
        style.id = `${idPrefix}-0`;
        style.textContent = cssString;
        document.head.appendChild(style);
        injectedRefs.current.push(style);
        return;
      }

      styleTags.forEach((tag, i) => {
        const validation = validateCSS(tag.textContent);
        if (!validation.valid) {
          return;
        }
        const style = document.createElement('style');
        style.id = `${idPrefix}-${i}`;
        style.textContent = tag.textContent;
        document.head.appendChild(style);
        injectedRefs.current.push(style);
      });
    };

    const injectAllScripts = (jsString, idPrefix) => {
      if (!jsString || jsString.trim() === '') return;
      const parser = new DOMParser();
      const doc = parser.parseFromString(jsString, 'text/html');
      const scriptTags = doc.querySelectorAll('script');

      if (scriptTags.length === 0 && jsString.trim().startsWith('<script') === false) {
        const validation = validateJS(jsString);
        if (!validation.valid) {
          return;
        }
        const script = document.createElement('script');
        script.id = `${idPrefix}-0`;
        script.textContent = jsString;
        document.head.appendChild(script);
        injectedRefs.current.push(script);
        return;
      }

      scriptTags.forEach((tag, i) => {
        if (tag.hasAttribute('src')) {
          const script = document.createElement('script');
          script.id = `${idPrefix}-${i}`;
          Array.from(tag.attributes).forEach(attr => {
            script.setAttribute(attr.name, attr.value);
          });
          document.head.appendChild(script);
          injectedRefs.current.push(script);
        } else {
          const validation = validateJS(tag.textContent);
          if (!validation.valid) {
            return;
          }
          const script = document.createElement('script');
          script.id = `${idPrefix}-${i}`;
          script.textContent = tag.textContent;
          document.head.appendChild(script);
          injectedRefs.current.push(script);
        }
      });
    };

    // ---- inject custom CSS/JS (your existing behavior) ----
    if (data.header_custom_css) injectAllStyles(data.header_custom_css, 'dynamic-header-css');
    if (data.header_custom_js)  injectAllScripts(data.header_custom_js, 'dynamic-header-js');

    if (data.body_open_custom_css) injectAllStyles(data.body_open_custom_css, 'dynamic-body-open-css');
    if (data.body_open_custom_js)  injectAllScripts(data.body_open_custom_js, 'dynamic-body-open-js');

    if (data.body_close_custom_css) injectAllStyles(data.body_close_custom_css, 'dynamic-body-close-css');
    if (data.body_close_custom_js)  injectAllScripts(data.body_close_custom_js, 'dynamic-body-close-js');

    // ---- NEW: apply theme color overrides to :root ----
    const overrides =
      data?.theme?.overrides ||
      data?.theme_colors?.overrides ||
      data?.theme_colors ||
      {};
    Object.entries(overrides).forEach(([key, val]) => {
      if (ALLOWED_VAR_KEYS.has(key) && isValidColor(val)) {
        document.documentElement.style.setProperty(`--${key}`, val);
        appliedVarKeysRef.current.push(key);
      }
    });

    // cleanup on unmount / data change
    return () => {
      injectedRefs.current.forEach(ref => ref?.parentNode?.removeChild(ref));
      injectedRefs.current = [];
      appliedVarKeysRef.current.forEach(key =>
        document.documentElement.style.removeProperty(`--${key}`)
      );
      appliedVarKeysRef.current = [];
    };
  }, [navbarData]);

  return {
    isLoaded: !!navbarData?.dataset?.data,
    data: navbarData?.dataset?.data || {},
  };
};
